'use client';

import Link from 'next/link';
import { Suspense } from 'react';

import { Drawer } from '@ui/components/common/drawer';

import { CategoryCard } from '../../components/features/category/categoryCard/CategoryCard';
import type { DisplayCategory } from './CategorySection';

interface CategoryDrawerProps {
  categories: DisplayCategory[];
  drawerHeadingText: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Client component for the category drawer on mobile
 * Wrapped in Suspense to handle URL-based state management
 */
function CategoryDrawerContent({ categories, drawerHeadingText, isOpen, onOpenChange }: CategoryDrawerProps) {
  return (
    <Drawer
      open={isOpen}
      onOpenChange={onOpenChange}
      trigger={<span className="hidden" />} // No trigger needed, controlled externally
      side="bottom"
      className="md:hidden max-h-[80vh] overflow-y-auto pt-0 pb-2 px-5 rounded-t-xl"
      showHandle={true}
      title={drawerHeadingText}
    >
      <div>
        <h3 className="text-lg font-medium mb-4">{drawerHeadingText}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {categories.map((category) => (
            <Link
              href={category.searchUrl}
              key={`drawer-${category.id}`}
              className="block"
              onClick={() => onOpenChange(false)} // Close drawer after selection
            >
              <CategoryCard
                categoryId={category.id}
                name={category.name}
                imageUrl={category.photo}
                variant={category.isSpecial ? 'rainbow' : 'default'}
              />
            </Link>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

export function CategoryDrawer(props: CategoryDrawerProps) {
  return (
    <Suspense fallback={null}>
      <CategoryDrawerContent {...props} />
    </Suspense>
  );
}
